import styled from 'styled-components';
import { Icon } from '@/icons';

export type Tier = 'verified' | 'trusted' | 'basic';

export interface TierBadgeProps {
  tier: Tier;
}

const TIER_LABEL: Record<Exclude<Tier, 'basic'>, string> = {
  verified: 'Verified Human',
  trusted: 'Trusted',
};

/* The scalloped badge-check, drawn in the accent. It was a solid accent disc
   with a stock check inside — the single most-copied verification mark on the
   web, and one specific product's trade dress. The scalloped outline is shared
   civic vocabulary (seals, stamps), and it is already the shape the supporter
   star is built to be distinguishable FROM, so the two can never collapse.
   Trusted is the same glyph at reduced opacity until a real verificationTier
   exists on the server. */
const Glyph = styled.span<{ $hollow: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.accentCyan};
  opacity: ${({ $hollow }) => ($hollow ? 0.7 : 1)};
`;

/**
 * TierBadge — verification-tier glyph. Scalloped badge-check in the accent =
 * Verified Human; the same at 70% = Trusted; nothing for Basic.
 * Bind via deriveTier() — 'trusted' is never emitted until a real
 * verificationTier field exists. The triad colours are NOT used here (tier
 * badges are teal); the triad lives only in the logo mark.
 */
export function TierBadge({ tier }: TierBadgeProps) {
  if (tier === 'basic') return null;
  const hollow = tier === 'trusted';
  return (
    <Glyph $hollow={hollow} role="img" aria-label={TIER_LABEL[tier]} data-tier={tier}>
      <Icon name="verify" size={16} />
    </Glyph>
  );
}
